/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe, NgClass, NgOptimizedImage } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatSliderModule } from "@angular/material/slider";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
    ButtonComponent,
    FormActionsComponent,
    FormComponent,
    FormInputComponent,
    FormOption,
    FormOptionGroup,
    FormSelectComponent,
    FormToggleGroupComponent,
    patternValidator,
    requiredValidator,
    SpinnerComponent
} from "typedb-cloud-framework";
import { ActivatedRoute, Router } from "@angular/router";
import { RegionPipe } from "../region.pipe";
import {
    burstableTooltip,
    ClusterPresetSpec,
    getPaymentOptions,
    MachineTypeOption,
    machineTypeToString,
    PaymentOptions,
    Provider,
    ProviderOptions,
    RegionOptions,
    regionToString, sortMachineType,
    StorageType,
    storageTypeToString,
    TypeDBVersion
} from "../../../concept/cluster-deployment-options";
import { ApplicationState } from "../../../service/application-state.service";
import { ClusterApi } from "../../../service/cluster/cluster-api.service";
import { TeamApi } from "../../../service/team/team-api.service";
import { SnackbarService } from "../../../service/snackbar.service";
import { AbstractControl, FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
    BehaviorSubject,
    combineLatest,
    concat,
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    first,
    map,
    Observable,
    of,
    ReplaySubject,
    shareReplay,
    startWith,
    Subject,
    switchMap,
    tap
} from "rxjs";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectChange, MatSelectModule } from "@angular/material/select";
import { MachineTypePipe } from "../machine-type.pipe";
import { Space } from "../../../concept/space";
import { Cluster, ClusterPrice, Frequency, SnapshotConfiguration, storageSizeToString } from "../../../concept/cluster";
import { SpaceApi } from "../../../service/space/space-api.service";
import { TeamController } from "../../../service/team/team-controller.service";
import { AnalyticsService } from "../../../service/analytics.service";
import { mergeListChanges } from "../../../concept/api-response";
import { DIALOG, SPACE_ID, SETUP } from "../../../framework/url-params";
import { renderThousandthCentsAsUSD, toSentenceCase } from "../../../util";
import { clusterDetailsPath } from "../../../routing/resource-paths";
import { idPattern, idPatternErrorText } from "typedb-web-common/lib";
import { MatDialog } from "@angular/material/dialog";
import { PaymentDetailsModalComponent } from "../../team/payment-details-modal/payment-details-modal.component";
import { StorageTypePipe } from "../storage-type.pipe";
import { MatInputModule } from "@angular/material/input";
import { generateName } from "./cluster-names";
import { MatStepperModule } from "@angular/material/stepper";
import { MatButtonModule } from "@angular/material/button";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { ProviderIdPipe } from "../provider-id.pipe";
import { DialogResult } from "../../../service/dialog.service";
import { StepperSelectionEvent } from "@angular/cdk/stepper";
import { CloudRegion, ProviderRegionData } from "typedb-web-schema/lib";
import { SanityService } from "../../../service/sanity.service";
import { FormService } from "../../../service/form.service";
import { IdentityAuthService } from "../../../service/authentication/identity-auth.service";
import { compareVersions } from 'compare-versions';

export enum ClusterCreationSteps {
    CLOUD = 0,
    CAPACITY = 1,
    CONFIGURATION = 2,
}

const serverCountOptions = [1, 3, 5, 7] as const;
type serverCountOption = typeof serverCountOptions[number];
const storageSliderValueToGBMap: { [key: number]: number } = {
    0: 10,
    1: 25,
    2: 50,
    3: 75,
    4: 100,
    5: 200,
    6: 400,
    7: 600,
    8: 800,
    9: 1000,
};
const gbToStorageSliderValueMap = Object.fromEntries(
    Object.entries(storageSliderValueToGBMap).map(([value, gb]) => [gb, value])
) as unknown as Record<number, number>
const freeOptions: { serverCount: serverCountOption, storageSlider: number, snapshotFrequency: Frequency }
    = { serverCount: 1, storageSlider: 0, snapshotFrequency: "disabled" };
interface ClusterPresetOption extends ClusterPresetSpec {
    id: string;
    name: string;
}
const frequencyOptions: FormOption<Frequency>[] = [
    { value: "disabled", viewValue: "Disabled" },
    { value: "hourly", viewValue: "Hourly" },
    { value: "daily", viewValue: "Daily" },
    { value: "weekly", viewValue: "Weekly" },
]
const unfilteredRetentionOptions: FormOption<number>[] = [
    { value: 7, viewValue: "7 days" },
    { value: 30, viewValue: "30 days" },
    { value: 365, viewValue: "365 days" },
]

@Component({
    selector: "tp-cluster-deployment-page",
    templateUrl: "./cluster-deployment-page.component.html",
    styleUrls: ["./cluster-deployment-page.component.scss"],
    imports: [
        MatFormFieldModule, MatInputModule, MatSelectModule,
        FormsModule, ReactiveFormsModule, AsyncPipe, ButtonComponent, FormSelectComponent, FormInputComponent,
        SpinnerComponent, FormComponent, FormToggleGroupComponent, NgClass, NgOptimizedImage,
        MatSliderModule, MatTooltipModule, RegionPipe, MachineTypePipe,
        MatStepperModule, MatButtonModule, MatSlideToggleModule, MatCheckboxModule, ProviderIdPipe
    ]
})
export class ClusterDeploymentPageComponent implements OnInit, OnDestroy {
    protected readonly NO_SAMPLE_DATASET_VALUE = "-";
    readonly team = this.app.requireCurrentTeam();
    readonly clusterDeploymentOptions$ = this.clusterApi.getClusterDeploymentOptions({ teamUuid: this.team.uuid, providerID: null, regionID: null }).pipe(first(), shareReplay(1));
    providers$: Observable<ProviderOptions[]> = this.clusterDeploymentOptions$.pipe(
        map((options) => options?.providers || [])
    );
    providersWithRegions$: Observable<ProviderOptions[]> = this.providers$.pipe(
        map((providers) => providers.filter(p => p.regions.length > 0))
    );
    regionGroups: FormOptionGroup<RegionOptions>[] = [];
    machineTypes: FormOption<MachineTypeOption>[] = [];
    storageTypes: FormOption<StorageType>[] = [];
    clusterPresets: FormOption<ClusterPresetOption & ClusterPrice>[] = [];
    readonly spaces$: Observable<Space[]> = this.teamCtl.listWritableSpacesSnapshot().pipe(shareReplay(1));
    readonly spaceOptions$: Observable<FormOption<Space>[]> = this.spaces$
        .pipe(map((spaces) => spaces.map(x => ({ value: x, viewValue: x.id }))));
    readonly existingClusters$ = new BehaviorSubject<Cluster[] | null>(null);
    readonly unsubExistingClusters$ = new Subject<void>();
    readonly payableProviderOptions$ = new ReplaySubject<ProviderOptions[]>(1);

    readonly isSubmitting$ = new BehaviorSubject(false);
    readonly form = this.formBuilder.group({
        id: ["", [
            patternValidator(idPattern, idPatternErrorText),
            requiredValidator,
            (control: AbstractControl) => this.clusterIDUniqueValidator(control)
        ]],
        space: [null as Space | null, [requiredValidator]],
        provider: [null as ProviderOptions | null, [requiredValidator]],
        region: [null as RegionOptions | null, [requiredValidator]],
        machineType: [null as MachineTypeOption | null, [requiredValidator]],
        serverCount: [null as serverCountOption | null, [requiredValidator]],
        storageType: [null as StorageType | null, [requiredValidator]],
        storageSlider: [null as number | null, [requiredValidator]],
        snapshotFrequency: [null as Frequency | null, [requiredValidator]],
        snapshotRetentionDays: [null as number | null, [requiredValidator]],
        typeDBVersion: [null as TypeDBVersion | null, [requiredValidator]],
        sampleDataset: [this.NO_SAMPLE_DATASET_VALUE, [requiredValidator]],
    });

    readonly requestForm = this.formBuilder.group({
        message: [""],
        provider: [null as 'gcp' | 'aws' | 'azure' | 'other' | null, [requiredValidator]],
        providerOther: [""],
        region: [null as CloudRegion | null],
        regionOther: [""],
    });

    readonly requestProviderOptions: FormOption<'gcp' | 'aws' | 'azure' | 'other'>[] = [
        { value: 'gcp', viewValue: 'Google Cloud Platform' },
        { value: 'aws', viewValue: 'Amazon Web Services' },
        { value: 'azure', viewValue: 'Microsoft Azure' },
        { value: 'other', viewValue: 'Other' },
    ];

    isRequestMode = false;
    providerRegionData?: ProviderRegionData;
    cloudRegionOptionGroups?: FormOptionGroup<CloudRegion>[];
    requestedRegionOption?: FormOption<CloudRegion>;
    requestFormSubmitting$ = new BehaviorSubject(false);
    retentionOptions$ = new BehaviorSubject(unfilteredRetentionOptions);

    readonly typeDBVersions$: Observable<FormOption<TypeDBVersion>[]> = this.clusterDeploymentOptions$.pipe(
        map((options) => (options?.typeDBVersions || [])
            .filter(x => !x.version.includes("alpha") && !x.version.includes("beta"))
            .sort((x, y) => compareVersions(y.version, x.version))
            .map(x => ({ value: x, viewValue: x.version })))
    );
    readonly typeDBVersionsExperimental$: Observable<FormOption<TypeDBVersion>[]> = this.clusterDeploymentOptions$.pipe(
        map((options) => (options?.typeDBVersions || [])
            .filter(x => x.version.includes("alpha") || x.version.includes("beta"))
            .sort((x, y) => compareVersions(y.version, x.version))
            .map(x => ({ value: x, viewValue: x.version })))
    );
    readonly sampleDatasets$: Observable<string[]> = this.form.controls.typeDBVersion.valueChanges.pipe(
        filter((version) => !!version),
        map((version) => version!.sampleDatasets),
        startWith([]),
        tap((sampleDatasets) => {
            if (sampleDatasets.length == 0 && this.form.controls.sampleDataset.enabled) {
                this.form.controls.sampleDataset.disable();
            } else if (sampleDatasets.length > 0 && this.form.controls.sampleDataset.disabled) {
                this.form.controls.sampleDataset.enable();
            }
        })
    )
    private readonly billableFields: (keyof typeof this.form.value)[] = ["provider", "region", "machineType", "serverCount", "storageSlider", /*"snapshotFrequency", "snapshotRetentionDays"*/];
    selectedPreset?: ClusterPresetOption;

    pricePerHourDetails$ = combineLatest(this.billableFields.map(field => concat(of(this.form.value[field]), this.form.get(field)!.valueChanges))).pipe(
        debounceTime(300),
        switchMap(() => this.fetchPrice()),
        shareReplay(1),
    );
    isFree$ = this.pricePerHourDetails$.pipe(map(x => x!= "loading" && x != null && x.running.thousandthCentsPerHour === 0));
    isPaid$ = this.pricePerHourDetails$.pipe(map(x => x!= "loading" && x != null && x.running.thousandthCentsPerHour != 0));
    pricePerHourString$ = this.pricePerHourDetails$.pipe(map(x => {
        if (x == null) return "--";
        else if (x === "loading") return undefined;
        else return this.pricePerHourToPerHourString(x.running.thousandthCentsPerHour);
    }));
    basePricePerHourString$ = this.pricePerHourDetails$.pipe(map(x => {
        if (x == null || x === "loading" || x.running.baseThousandthCentsPerHour == undefined || x.running.baseThousandthCentsPerHour == x.running.thousandthCentsPerHour) return undefined;
        else return this.pricePerHourToPerHourString(x.running.baseThousandthCentsPerHour!);
    }));
    snapshotPriceString$ = this.pricePerHourDetails$.pipe(
        map(x => {
            if (x == null || x === "loading" || x.snapshots == undefined) return undefined;
            else return this.pricePerHourToPerMonthString(x.snapshots.thousandthCentsPerHour)
        })
    );
    baseSnapshotPriceString$ = this.pricePerHourDetails$.pipe(
        map(x => {
            if (x == null || x === "loading" || x.snapshots == undefined || x.snapshots.baseThousandthCentsPerHour == undefined || x.snapshots.baseThousandthCentsPerHour == x.snapshots.thousandthCentsPerHour) return undefined;
            else return this.pricePerHourToPerMonthString(x.snapshots.baseThousandthCentsPerHour!);
        })
    );
    pricePerMonthString$ = this.pricePerHourDetails$.pipe(map(x => {
        if (x == null) return "--";
        else if (x === "loading") return undefined;
        // else if (
        //     this.form.value.snapshotFrequency !== "disabled" && x.snapshots && this.form.value.storageSlider != null
        // ) {
        //     const snapshotConfig: SnapshotConfiguration = {
        //         frequency: this.form.value.snapshotFrequency!,
        //         retentionDays: this.form.value.snapshotRetentionDays!,
        //     }
        //
        //     let snapshotCount: number
        //     let snapshotChangeFactor: number;
        //     if (snapshotConfig.frequency === "hourly") {
        //         snapshotCount = snapshotConfig.retentionDays * 24;
        //         snapshotChangeFactor = 0.01;
        //     } else if (snapshotConfig.frequency === "daily") {
        //         snapshotCount = snapshotConfig.retentionDays;
        //         snapshotChangeFactor = 0.05;
        //     } else if (snapshotConfig.frequency === "weekly") {
        //         snapshotCount = snapshotConfig.retentionDays/7;
        //         snapshotChangeFactor = 0.1
        //     }
        //
        //     const storageGB = this.storageSliderValueToGB(this.form.value.storageSlider);
        //     const estimatedSnapshotSize = storageGB * 0.25 + snapshotCount! * storageGB * snapshotChangeFactor!; // estimate based on an underlying level of storage usage with subsequent snapshots being incremental
        //     return this.pricePerHourToPerMonthString(x.running.thousandthCentsPerHour + x.snapshots.thousandthCentsPerHour * estimatedSnapshotSize);
        // }
        else return this.pricePerHourToPerMonthString(x.running.thousandthCentsPerHour);
    }));
    readonly canSubmit$ = combineLatest([this.isSubmitting$, this.pricePerHourDetails$]).pipe(
        map(([isSubmitting, priceDetails]) => !isSubmitting && priceDetails != null && priceDetails != "loading")
    );
    providerSectionReady = false;
    readonly isFreeTierSelected$ = new ReplaySubject<boolean>(1);
    memorisedFieldsBeforeChoosingFreeTier = freeOptions;
    serverCountLocked$ = new ReplaySubject<boolean>(1);
    readonly serverCountTooltip$ = new ReplaySubject<string>(1);
    serverCounts$ = this.serverCountLocked$.pipe(
        map((serverCountLocked) => {
            if (serverCountLocked) return serverCountOptions.map(x => ({ value: x, viewValue: x === 1 ? "1*" : x.toString() }));
            else return serverCountOptions.map(x => ({ value: x }));
        })
    );
    readonly teamAlreadyHasFreeCluster$ = this.teamApi.getFreeClusterCount(this.team.uuid).pipe(
        map(x => x > 0),
        distinctUntilChanged(),
        shareReplay(1),
    );
    readonly hasUsablePaymentMethod$ = combineLatest([this.payableProviderOptions$, this.form.valueChanges]).pipe(
        filter(() => !!this.form.value.provider && !!this.form.value.region && !!this.form.value.machineType),
        map(([payable]) =>
            getPaymentOptions(payable, this.form.value.provider!.id, this.form.value.region!.id, this.form.value.machineType!.vendorId).length > 0
        )
    );
    readonly mustAddPaymentMethod$ = combineLatest([this.isFreeTierSelected$, this.hasUsablePaymentMethod$]).pipe(
        map(([isFreeTierSelected, hasUsablePaymentMethod]) => !isFreeTierSelected && !hasUsablePaymentMethod),
        shareReplay(1),
    );
    readonly showAskAdminForPaymentMethodPrompt$ = combineLatest([this.mustAddPaymentMethod$, this.app.hasTeamAdminAccess$]).pipe(
        map(([mustAddPaymentMethod, isAdmin]) => mustAddPaymentMethod && !isAdmin),
    );

    currentStep: ClusterCreationSteps = ClusterCreationSteps.CLOUD;
    advancedConfig = false;
    checkoutShowDetailedSpecs = false;
    checkoutShowMonthlyEstimate = false;
    idGenerateCount = 0;
    animalIconOptions: string[] = ["fa-hippo", "fa-rabbit", "fa-pig", "fa-squirrel", "fa-cat", "fa-mouse-field", "fa-monkey", "fa-turtle"];
    animalIcon: string = this.animalIconOptions[0];

    constructor(
        private app: ApplicationState, private formBuilder: FormBuilder, private route: ActivatedRoute,
        private clusterApi: ClusterApi, private spaceApi: SpaceApi, private teamApi: TeamApi,
        private router: Router, private snackbar: SnackbarService, private dialog: MatDialog,
        private teamCtl: TeamController, private analytics: AnalyticsService, private sanity: SanityService,
        private formService: FormService, private identityAuth: IdentityAuthService,
    ) {}

    ngOnInit() {
        this.initExistingClustersLoader();
        this.fetchPayableProviders(false);
        this.restrictSpecsWhenOnFreeTier();
        this.autoUpdateRegionAndMachines();
        this.autoUpdateSampleDatasets();
        this.prepopulateSpace();
        this.prepopulateProviderAndTypeDBVersion();
        this.generateAndFillID();
        this.updateServerCountInformation();
        this.autoUpdateProviderRegionData();
        this.autoUpdateRetentionFromFrequency();
    }

    ngOnDestroy() {
        this.unsubExistingClusters$.next();
    }

    private initExistingClustersLoader() {
        this.form.controls.space.valueChanges.pipe(
            tap(() => this.unsubExistingClusters$.next()),
            switchMap((space) => this.spaceApi.listClusters(
                space!.uuid,
                { pagination: { offset: 0, limit: 1000 }, sorting: { attributeType: "id" } },
                this.unsubExistingClusters$
            )),
        ).subscribe((res) => {
            const mergeResult = mergeListChanges(this.existingClusters$.value, res);
            if (mergeResult.result === "shouldResync") {
                console.warn(`Received update is not valid in the current state; forcing a full resync`);
                this.initExistingClustersLoader();
                return;
            }
            this.existingClusters$.next(mergeResult.data);
            this.form.controls.id.updateValueAndValidity();
        });
    }

    fetchPayableProviders(manualTrigger: boolean) {
        const team = this.team;
        if (manualTrigger) this.isSubmitting$.next(true);
        this.teamApi.getPayableClusterDeploymentOptions(team.uuid).subscribe((payableProviders) => {
            this.payableProviderOptions$.next(payableProviders);
            this.isSubmitting$.next(false);
            if (manualTrigger) {
                const selectedProviderID = this.form.value.provider!.id;
                const selectedRegionID = this.form.value.region?.id;
                const selectedMachineTypeID = this.form.value.machineType?.vendorId;
                if (getPaymentOptions(payableProviders, selectedProviderID, selectedRegionID, selectedMachineTypeID).length > 0) {
                    this.snackbar.success("Payment method validated successfully");
                } else {
                    this.snackbar.warnPersistent(`No valid payment method found. Please contact a team administrator.`);
                }
            }
        });
    }

    private autoUpdateRegionAndMachines() {
        // TODO: should use the same logic as setup/project. This would require a data migration as setup/project
        //       expects a different data layout
        this.form.controls.provider.valueChanges.subscribe((provider) => {
            const continentNames = [...new Set(provider!.regions
                .sort((x, y) => x.continentOrdinal - y.continentOrdinal)
                .map(x => x.continentName)
            )];
            const regionMap = Object.fromEntries(continentNames.map(x => [x, [] as FormOption<RegionOptions>[]]));
            provider!.regions.sort((a, b) => a.vendorId.localeCompare(b.vendorId)).forEach(region => {
                const formOption = this.regionOptionOf(region);
                regionMap[region.continentName].push(formOption);
            });
            this.regionGroups = Object.entries(regionMap).map(([name, options]) => ({ name, options }));
            if (this.regionGroups.length > 0) {
                this.form.patchValue({ region: this.regionGroups
                            .flatMap(x => x.options)
                            .find(x => x.value.isFreeAvailable)?.value
                        || this.regionGroups[0].options[0].value
                });
            }

            if (provider!.regions.length == 0) {
                this.updateCloudRegionRequestOptions(provider!.id);
            }
        });

        combineLatest([
            this.teamAlreadyHasFreeCluster$, this.form.controls.region.valueChanges, this.form.controls.typeDBVersion.valueChanges
        ]).subscribe(([alreadyHasFree, r, v]) => {
            const region = (r ?? this.form.value.region)!;
            const version = (v ?? this.form.value.typeDBVersion);

            const currentMachineTypeCPU = this.form.value.machineType?.cpu;

            this.machineTypes = region.machineTypes
                .filter((machineType) => !machineType.isFree || !alreadyHasFree)
                .sort(sortMachineType)
                .map(this.machineTypeOptionOf);
            this.storageTypes = region.storageTypes
                .sort((a, b) => a.vendorId.localeCompare(b.vendorId))
                .map(this.storageTypeOptionOf);
            const singleNodeOnly= !!(version?.singleNodeOnly);
            const sortedPresets = region.clusterPresets
                .map((clusterPreset) => {
                    if (singleNodeOnly) return { ...clusterPreset.singleNodeSpec, id: clusterPreset.id, name: clusterPreset.name}
                    else return { ...clusterPreset.multiNodeSpec, id: clusterPreset.id, name: clusterPreset.name}
                })
                .sort((a, b) => sortMachineType(a.machineType, b.machineType) || (a.serverCount - b.serverCount));

            if (this.advancedConfig) {
                const newMachineTypeIndex = Math.max(this.machineTypes.findIndex((machineType) => machineType.value.cpu == currentMachineTypeCPU), 0);
                this.form.patchValue({
                    machineType: this.machineTypes[newMachineTypeIndex].value,
                    storageType: this.storageTypes[0].value,
                });

                if (this.form.value.serverCount! > 1 && version?.singleNodeOnly) {
                    this.form.patchValue({ serverCount: 1 });
                }
            }

            combineLatest(sortedPresets.map((preset) =>
                this.clusterApi.getClusterPresetPriceThousandthCentsPerHour({ regionId: region.id, presetId: preset.id, teamUuid: this.team.uuid, singleNode: singleNodeOnly })
                    .pipe(map((price) => this.clusterPresetOptionOf(preset, price)))
            )).subscribe((presets) => {
                this.clusterPresets = presets;
                if (!this.advancedConfig) {
                    const equivalentPreset = sortedPresets.find((x) => x.name == this.selectedPreset?.name);
                    if (equivalentPreset) {
                        this.selectPreset(equivalentPreset);
                    } else if (this.form.controls.machineType.touched || this.currentStep >= ClusterCreationSteps.CAPACITY) {
                        this.selectPreset(sortedPresets[0]);
                    }
                }
            });
        });
    }

    private autoUpdateSampleDatasets() {
        combineLatest([this.isFreeTierSelected$, this.sampleDatasets$]).subscribe(([isFreeTierSelected, sampleDatasets]) => {
            if (isFreeTierSelected && sampleDatasets.length) {
                this.form.patchValue({ sampleDataset: sampleDatasets[0] });
            } else {
                this.form.patchValue({ sampleDataset: this.NO_SAMPLE_DATASET_VALUE });
            }
        });
    }

    private prepopulateProviderAndTypeDBVersion() {
        combineLatest([this.providers$, this.payableProviderOptions$]).pipe(first()).subscribe(([providers, payable]) => {
            const preferredProvider = providers.find(x => x.isFreeAvailable && payable.map(y => y.id).includes(x.id))
                || providers.find(x => payable.map(y => y.id).includes(x.id))
                || providers.find(x => x.isFreeAvailable)
                || providers[0];
            this.form.patchValue({ provider: preferredProvider });
            this.providerSectionReady = true;
        });
        this.typeDBVersions$.subscribe((versions) => {
            if (!versions.some((version) => this.form.value.typeDBVersion?.version == version.value.version)) {
                this.form.patchValue({ typeDBVersion: versions[0].value });
            }
        });
    }

    private prepopulateSpace() {
        combineLatest([this.route.queryParamMap.pipe(first()), this.spaces$.pipe(first())]).subscribe(([params, spaces]) => {
            const requestedSpaceID = params.get(SPACE_ID);
            if (requestedSpaceID != null) {
                const space = spaces.find(x => x.id === requestedSpaceID);
                if (space) {
                    this.form.patchValue({ space });
                    return;
                } else {
                    this.snackbar.warn(`Space with ID '${requestedSpaceID}' not found`);
                }
            }
            if (spaces.length) this.form.patchValue({ space: spaces.find(x => x.id === "default") || spaces[0] })
            else this.snackbar.warn(`This team has no spaces. Please create a space first.`);
        });
    }

    private updateServerCountInformation() {
        this.form.controls.typeDBVersion.valueChanges.subscribe((version) => {
            this.serverCountLocked$.next(!!version?.singleNodeOnly);
            if (version?.singleNodeOnly) this.serverCountTooltip$.next("Multi-node support is available from 3.14 onwards.")
            else return this.serverCountTooltip$.next("")
        });
    }

    private restrictSpecsWhenOnFreeTier() {
        this.form.controls.machineType.valueChanges.pipe(
            filter((machineType) => !!machineType),
            map((machineType) => machineType!.isFree)
        ).subscribe((isFree) => {
            this.isFreeTierSelected$.next(isFree);
        });

        this.isFreeTierSelected$.pipe(
            distinctUntilChanged(),
        ).subscribe((isFreeTierSelected) => {
            if (isFreeTierSelected) {
                if (this.form.value.serverCount && this.form.value.storageSlider && this.form.value.snapshotFrequency && this.form.value.snapshotRetentionDays) {
                    this.memorisedFieldsBeforeChoosingFreeTier = {
                        serverCount: this.form.value.serverCount,
                        storageSlider: this.form.value.storageSlider,
                        snapshotFrequency: this.form.value.snapshotFrequency,
                    };
                }
                this.form.patchValue(freeOptions);
            } else {
                this.form.patchValue(this.memorisedFieldsBeforeChoosingFreeTier);
            }
        });

        this.form.controls.snapshotFrequency.valueChanges.subscribe((value) => {
            if (value != "disabled" && value != null) this.updateToPaidMachineType();
        });
        this.form.controls.storageSlider.valueChanges.subscribe((value) => {
            if (value != 0 && value != null) this.updateToPaidMachineType();
        });
        this.form.controls.serverCount.valueChanges.subscribe((value) => {
            if (value != 1) this.updateToPaidMachineType();
        });
    }

    private clusterIDUniqueValidator(control: AbstractControl<string>) {
        if (!this.existingClusters$.value) return null;
        if (this.existingClusters$.value.some(x => x.id === control.value)) {
            return { errorText: `The cluster name '${control.value}' is already in use in this space` };
        } else return null;
    }

    regionOptionOf(region: RegionOptions): FormOption<RegionOptions> {
        return { value: region, viewValue: regionToString(region) };
    }

    machineTypeOptionOf(machine: MachineTypeOption): FormOption<MachineTypeOption> {
        return { value: machine, viewValue: machineTypeToString(machine) };
    }

    storageTypeOptionOf(storage: StorageType): FormOption<StorageType> {
        return { value: storage, viewValue: storageTypeToString(storage) };
    }

    clusterPresetOptionOf(preset: ClusterPresetOption, price: ClusterPrice): FormOption<ClusterPresetOption & ClusterPrice> {
        return { value: { ...preset, ...price }, viewValue: preset.name };
    }

    providerCardClasses(provider: ProviderOptions): { [clazz: string]: boolean } {
        return {
            "card-selected": this.form.value.provider?.id === provider.id && !this.isRequestMode,
            [`provider-card-${provider.id}`]: true,
            // "provider-card-free-available": provider.isFreeAvailable, // TODO: bring back once something isn't free-available
        };
    }

    presetCardClasses(preset: ClusterPresetOption): { [clazz: string]: boolean } {
        return {
            "card-selected": this.selectedPreset?.id == preset.id && !this.advancedConfig,
            [`preset-card-${preset.id.split("-")[0]}`]: true,
        };
    }

    providerImageUrl(provider: Provider): string {
        return `/assets/logo/${provider.id}.svg`;
    }

    regionFlagUrl(countryCode: string): string {
        return `/assets/flags/${countryCode.toLowerCase()}.svg`;
    }

    selectProvider(provider: ProviderOptions) {
        this.form.patchValue({ provider });
    }

    selectPreset(preset: ClusterPresetOption) {
        this.selectedPreset = preset;
        this.teamAlreadyHasFreeCluster$.pipe(first()).subscribe((teamAlreadyHasFreeCluster) => this.updateFormFromPresetValues(preset, teamAlreadyHasFreeCluster));
    }

    updateFormFromPresetValues(preset: ClusterPresetOption, teamAlreadyHasFreeCluster: boolean) {
        const machineTypes = this.machineTypes.filter(x => x.value.vendorId == preset.machineType.vendorId);
        const machineType = teamAlreadyHasFreeCluster ? machineTypes[0] : machineTypes.find(x => x.value.isFree == this.presetCanBeFree(preset)) || machineTypes[0];
        this.form.patchValue({
            machineType: machineType.value,
            storageType: this.storageTypes.find(x => x.value.vendorId == preset.storageType.vendorId)?.value,
            storageSlider: this.storageSliderGBToValue(preset.storageGB),
            serverCount: preset.serverCount as serverCountOption,
            snapshotFrequency: machineType.value.isFree ? "disabled" : preset.snapshotConfiguration.frequency,
            snapshotRetentionDays: preset.snapshotConfiguration.retentionDays,
        });
    }

    updateToPaidMachineType() {
        if (!this.form.value.machineType!.isFree) return;
        this.form.patchValue({
            machineType: this.machineTypes.find(mt => mt.value.vendorId === this.form.value.machineType!.vendorId && !mt.value.isFree)!.value
        });
    }

    presetCanBeFree(preset: ClusterPresetOption): boolean {
        return preset.serverCount == 1 && preset.storageGB == storageSliderValueToGBMap[0];
    }

    presetSnapshotFrequencyToDisplayString(preset: ClusterPresetOption): string {
        return toSentenceCase(preset.snapshotConfiguration.frequency);
    }

    storageSliderGBToValue(gb: number): number {
        return gbToStorageSliderValueMap[gb];
    }

    storageSliderValueToGB(rawValue: number): number {
        return storageSliderValueToGBMap[rawValue];
    }

    private fetchPrice(): Observable<ClusterPrice | "loading" | null> {
        const values = this.form.getRawValue();
        if (
            this.billableFields.some(x => values[x] == null) ||
            !values["provider"]?.regions?.some((region) => region.vendorId == values["region"]?.vendorId) ||
            !values["region"]?.machineTypes?.some((machineType) => machineType.vendorId == values["machineType"]?.vendorId) ||
            !values["region"]?.storageTypes?.some((storageType) => storageType.vendorId == values["storageType"]?.vendorId)
        ) return of(null);
        const cluster = this.clusterFromFormValues();

        return this.isFreeTierSelected$.pipe(
            first(),
            switchMap((isFreeTier) => {
                if (isFreeTier) return of({ running: { thousandthCentsPerHour: 0 }, paused: { thousandthCentsPerHour: 0 } });
                else return this.clusterApi.getClusterPriceThousandthCentsPerHour({
                    cluster: cluster,
                    teamUuid: this.team.uuid,
                }).pipe(startWith<ClusterPrice | "loading">("loading"));
            }),
        );
    }

    pricePerHourToPerHourString(pricePerHourThousandthCents: number): string {
        if (pricePerHourThousandthCents === 0) return `Free`;
        else return renderThousandthCentsAsUSD(pricePerHourThousandthCents);
    }

    pricePerHourToPerMonthString(pricePerHourThousandthCents: number): string {
        if (pricePerHourThousandthCents === 0) return `Free`;
        else return renderThousandthCentsAsUSD(pricePerHourThousandthCents * 720);
    }

    clusterFromFormValues() {
        const form = this.form.value;
        const id = form.id!;
        const space = form.space!;
        const [provider, region] = [form.provider!, form.region!];
        const [machineType, serverCount, storageType, storageGB]
            = [form.machineType!, form.serverCount!, form.storageType!, this.storageSliderValueToGB(form.storageSlider!)]
        const typeDBVersion = form.typeDBVersion!;
        const sampleDataset = this.form.value.sampleDataset === this.NO_SAMPLE_DATASET_VALUE ? undefined : (this.form.value.sampleDataset ?? undefined);
        const snapshotConfiguration = {
            frequency: this.form.value.snapshotFrequency!,
            retentionDays: this.form.value.snapshotRetentionDays!,
        };

        return { id, space, provider, region, machineType, serverCount, storageType, storageGB, typeDBVersion, sampleDataset, snapshotConfiguration };
    }

    submit() {
        const cluster = this.clusterFromFormValues();
        this.clusterApi.deployCluster(cluster, undefined).subscribe({
            next: () => {
                this.analytics.google.reportAdConversion(cluster.machineType.isFree ? "deployFreeCluster" : "deployPaidCluster");
                this.router.navigate([clusterDetailsPath(cluster, this.team)], { queryParams: { [DIALOG]: SETUP } });
            },
            error: () => {
                this.isSubmitting$.next(false);
            }
        });
    }

    cancel() {
        this.router.navigate([".."], { relativeTo: this.route });
    }

    advance() {
        this.currentStep++;
    }

    return() {
        this.currentStep--;
    }

    isIdValid(id: string) {
        return !this.form.controls.id.validator?.call(this, new FormControl(id));
    }

    generateAndFillID() {
        this.idGenerateCount++;
        if (this.idGenerateCount % 10 == 0) {
            const animalIndex = Math.floor(Math.random() * this.animalIconOptions.length);
            this.animalIcon = this.animalIconOptions[animalIndex];
        }
        this.form.patchValue({ id: generateName((id) => this.isIdValid(id))});
    }

    toggleAdvanced() {
        this.advancedConfig = !this.advancedConfig
        if (!this.advancedConfig && this.selectedPreset) this.selectPreset(this.selectedPreset);
    }

    onStepChange(stepChange: StepperSelectionEvent) {
        if (stepChange.selectedIndex >= ClusterCreationSteps.CAPACITY && !this.advancedConfig && !this.selectedPreset && this.clusterPresets.length > 0) {
            this.selectPreset(this.clusterPresets[0].value);
        }
        this.idGenerateCount = 0;
    }

    get machineTypeFieldTitle(): string {
        return this.machineTypes.find(x => x.value === this.form.value.machineType)?.viewValue ?? "Select an option";
    }

    get storageSliderIndicatorText(): string {
        let storageString = this.storageSliderCurrentValueToString;
        if (this.form.value.machineType?.isFree) storageString += "*";
        return storageString;
    }

    get storageSliderCurrentValueToString(): string {
        if (this.form.value.storageSlider != null) return this.storageSliderValueToString(this.form.value.storageSlider!);
        else return "--";
    }

    storageSliderValueToString(rawValue: number): string {
        const gb = storageSliderValueToGBMap[rawValue];
        return storageSizeToString(gb);
    }

    openPaymentDetailsDialog() {
        this.dialog.open<PaymentDetailsModalComponent, any, DialogResult>(PaymentDetailsModalComponent).afterClosed().subscribe(result => {
            if (result === "ok") {
                this.fetchPayableProviders(true);
                this.snackbar.success("Payment method added successfully");
            }
            else if (result == null || result === "cancelled") return;
            else this.snackbar.errorPersistent(`Error setting up payment method: ${result.error}`);
        });
    }

    willCreditsApply$(
        providerId: string | undefined, regionId: string | undefined, machineTypeVendorId: string | undefined
    ): Observable<boolean> {
        if (!providerId) return of(false);
        return combineLatest([this.payableProviderOptions$, this.isPaid$]).pipe(
            map(([payableOptions, isPaid]) =>
                isPaid && getPaymentOptions(payableOptions, providerId, regionId, machineTypeVendorId).includes(PaymentOptions.CREDITS)
            ),
            startWith(false),
            shareReplay(1),
        );
    }

    versionTooltip(version: TypeDBVersion, currentSingleNodeOnly: boolean) {
        let tooltip = ""
        if (version.singleNodeOnly) tooltip += "Multi-node support is available from 3.14 onwards.";
        else if (version.singleNodeOnly != currentSingleNodeOnly) tooltip += "Selecting this version may change your cluster capacity.";
        return tooltip;
    }

    enterRequestMode(preselectedProviderId?: string) {
        this.isRequestMode = true;
        this.requestForm.reset();
        if (preselectedProviderId) {
            const providerValue = this.requestProviderOptions.find(p => p.value === preselectedProviderId)?.value;
            if (providerValue) {
                this.requestForm.patchValue({ provider: providerValue });
                this.onRequestProviderChange(providerValue);
            }
        }
    }

    exitRequestMode() {
        this.isRequestMode = false;
        this.requestForm.reset();
    }

    onRequestProviderChange(providerId: 'gcp' | 'aws' | 'azure' | 'other' | null) {
        this.requestForm.patchValue({ region: null, regionOther: "" });
        if (providerId && providerId !== 'other') {
            this.updateCloudRegionRequestOptions(providerId);
        } else {
            this.cloudRegionOptionGroups = undefined;
        }
    }

    isRequestFormValid(): boolean {
        const form = this.requestForm.value;
        if (!form.provider) return false;
        if (form.provider === 'other') {
            return !!form.providerOther?.trim() && !!form.regionOther?.trim();
        }
        if (form.region === null && !form.regionOther?.trim()) return false;
        if (form.region === null && form.regionOther?.trim()) return true;
        return !!form.region;
    }

    submitRequestForm() {
        this.requestFormSubmitting$.next(true);
        this.requestForm.disable();

        let requestedProvider: string;
        let requestedRegion: string;

        if (this.requestForm.value.provider === 'other') {
            requestedProvider = this.requestForm.value.providerOther || 'other';
            requestedRegion = this.requestForm.value.regionOther || '';
        } else {
            requestedProvider = this.requestForm.value.provider || '';
            if (this.requestForm.value.regionOther?.trim()) {
                requestedRegion = this.requestForm.value.regionOther;
            } else {
                const region = this.requestForm.value.region;
                requestedRegion = region ? `${region.location} (${region.vendorId})` : '';
            }
        }

        this.formService.submit(this.formService.cloudRegionRequestFormID, {
            email: this.identityAuth.currentUser!.email!,
            requested_provider: requestedProvider,
            requested_region: requestedRegion,
            region_request_message: this.requestForm.value.message,
        }).pipe(delay(300)).subscribe(() => {
            this.snackbar.success(`Your request has been sent!`);
            this.requestFormSubmitting$.next(false);
            this.requestForm.enable();
            this.exitRequestMode();
        });
    }

    private autoUpdateProviderRegionData() {
        this.sanity.providerRegionData.pipe(first()).subscribe((data) => {
            this.providerRegionData = data;
        });
    }

    private autoUpdateRetentionFromFrequency() {
        this.form.controls.snapshotFrequency.valueChanges.pipe(startWith(null)).subscribe((frequency) => {
            if (frequency === "hourly") {
                if (this.form.value.snapshotRetentionDays === 365) this.form.patchValue({ snapshotRetentionDays: 30 });
                this.retentionOptions$.next(unfilteredRetentionOptions.filter(x => x.value != 365));
            } else if (frequency === "weekly") {
                if (this.form.value.snapshotRetentionDays === 7) this.form.patchValue({ snapshotRetentionDays: 30 });
                this.retentionOptions$.next(unfilteredRetentionOptions.filter(x => x.value != 7));
            } else this.retentionOptions$.next(unfilteredRetentionOptions);
        });
    }

    private cloudRegionOptionOf(region: CloudRegion, providerId: "gcp" | "aws" | "azure"): FormOption<CloudRegion> {
        let viewValue: string;
        switch (providerId) {
            case "gcp": viewValue = `${region.location} (${region.vendorId})`; break;
            case "aws": viewValue = `${region.location} (${region.vendorId})`; break;
            case "azure": viewValue = region.vendorId; break;
        }
        return { value: region, viewValue: viewValue };
    }

    private updateCloudRegionRequestOptions(providerId: string) {
        const continentNames = [...new Set(this.providerRegionData!.continents
            .sort((x, y) => x.ordinal - y.ordinal)
            .map(x => x.name)
        )];
        const regionMap = Object.fromEntries(continentNames.map(x => [x, [] as FormOption<CloudRegion>[]]));
        const provider = this.providerRegionData!.providers.find(x => x.id === providerId);
        if (!provider) throw `Invalid cloud provider ID: ${providerId}`;
        provider!.regions.forEach(region => {
            const formOption = this.cloudRegionOptionOf(region, providerId as "gcp" | "aws" | "azure");
            regionMap[region.country.continent].push(formOption);
        });
        this.cloudRegionOptionGroups = Object.entries(regionMap).map(([name, options]) => ({ name, options }));
    }

    changeSelectedCloudRegion(change: MatSelectChange) {
        this.requestedRegionOption = this.cloudRegionOptionGroups!.flatMap(x => x.options)
            .find(x => x.value.vendorId === (change.value as CloudRegion).vendorId);
    }

    protected readonly storageSizeToString = storageSizeToString;
    protected readonly ClusterCreationSteps = ClusterCreationSteps;
    protected readonly frequencyOptions = frequencyOptions;
    protected readonly burstableTooltip = burstableTooltip;
}
